import React, {useEffect, useState} from 'react';
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {Password} from "primereact/password";
import {QRCodeSVG} from "qrcode.react";

import {UserService} from "../../service/UserService";
import {AppContext} from "../../HomePage";
import {getKeyValue} from "../../utils/Utils";
import {Loading} from "../../utils/InlineComponents";
import i18n from "../../i18n";

const TwoFactor = (props) => {

    const {t, toast, setError} = React.useContext(AppContext);
    const userService = new UserService();

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [enrollment, setEnrollment] = useState(null);   // {method, secret, provisioning_uri, masked_email}
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [disabled, setDisabled] = useState(false);
    const [confirmingDisable, setConfirmingDisable] = useState(false);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = () => {
        setLoading(true);
        setLoadFailed(false);
        userService.getTwoFactor().then(data => {
            setStatus(data);
            setLoading(false);
        }).catch(error => {
            setError(error);
            setError(null);
            setLoadFailed(true);
            setLoading(false);
        });
    };

    const fail = (error) => {
        const data = error.response && error.response.data;
        const key = data && data.error_key;
        const translated = key ? t(key) : null;
        if (translated && translated !== key) {
            toast.current.show({severity: 'error', summary: t('message_detail.error'), detail: translated});
        } else {
            setError(error);
            setError(null);
        }
        setDisabled(false);
    };

    const startSetup = (method) => {
        setDisabled(true);
        setCode('');
        userService.setupTwoFactor(method).then(data => {
            setEnrollment(data);
            setDisabled(false);
        }).catch(fail);
    };

    const activate = () => {
        setDisabled(true);
        userService.activateTwoFactor(code.trim()).then(() => {
            toast.current.show({severity: 'success', summary: t('account.two_factor.title'),
                                detail: t('api.message.two_factor.enabled')});
            setEnrollment(null);
            setCode('');
            setDisabled(false);
            load();
        }).catch(fail);
    };

    const disable = () => {
        setDisabled(true);
        userService.disableTwoFactor(password).then(() => {
            toast.current.show({severity: 'success', summary: t('account.two_factor.title'),
                                detail: t('api.message.two_factor.disabled')});
            setPassword('');
            setConfirmingDisable(false);
            setDisabled(false);
            load();
        }).catch(fail);
    };

    const forgetDevices = () => {
        setDisabled(true);
        userService.forgetTrustedDevices().then(() => {
            toast.current.show({severity: 'success', summary: t('account.two_factor.title'),
                                detail: t('api.message.two_factor.devices_forgotten')});
            setDisabled(false);
            load();
        }).catch(fail);
    };

    const switchMethod = () => {
        const otherMethod = status.method === 'totp' ? 'email' : 'totp';
        startSetup(otherMethod);
    };

    if (loading)
        return <Loading/>;

    if (loadFailed && !status) {
        return (
            <div className="acc-section acc-narrow p-fluid">
                <p className="acc-section-title">{t('account.two_factor.title')}</p>
                <p className="acc-section-sub">{t('account.two_factor.load_error')}</p>
                <div className="tf-actions">
                    <Button label={t('actions.retry')} icon="pi pi-refresh"
                            className="p-button-outlined p-button-sm" onClick={load}/>
                </div>
            </div>
        );
    }

    const methodLabel = (method) => method === 'totp'
        ? t('account.two_factor.method_totp')
        : t('account.two_factor.method_email');

    const enabledSince = () => {
        if (!status || !status.enabled_at)
            return '';
        const lang = (i18n.language || 'en').split('-')[0];
        return new Date(status.enabled_at)
            .toLocaleDateString(lang, {day: 'numeric', month: 'long', year: 'numeric'});
    };

    return (
        <div className="acc-section acc-narrow p-fluid">
            <p className="acc-section-title">{t('account.two_factor.title')}</p>
            <p className="acc-section-sub">{t('account.two_factor.sub')}</p>

            {status && status.is_enabled && !enrollment &&
            <div>
                <div className="tf-status">
                    <i className="pi pi-shield"/>
                    <div className="tf-status-main">
                        <div className="tf-status-title">{t('account.two_factor.status_on')}</div>
                        <div className="tf-status-meta">
                            {methodLabel(status.method)}
                            {status.enabled_at &&
                                ' · ' + t('account.two_factor.active_since', {date: enabledSince()})}
                        </div>
                    </div>
                </div>

                <div className="tf-actions tf-method-actions">
                    <Button label={status.method === 'totp'
                                ? t('account.two_factor.switch_to_email')
                                : t('account.two_factor.switch_to_totp')}
                            icon="pi pi-refresh" className="p-button-outlined p-button-sm"
                            onClick={switchMethod} disabled={disabled}/>
                    {/* Re-enrolling the SAME method was previously impossible: a user with a
                        new phone had to detour through email and back. */}
                    {status.method === 'totp' &&
                        <Button label={t('account.two_factor.new_phone')} icon="pi pi-mobile"
                                className="p-button-text p-button-sm"
                                onClick={() => startSetup('totp')} disabled={disabled}/>}
                </div>
                <p className="acc-hint tf-switch-hint">{t('account.two_factor.switch_hint')}</p>

                <p className="acc-cat">{t('account.two_factor.devices_heading')}</p>
                <div className="tf-line">
                    <span className="tf-line-text">
                        {status.trusted_device_count > 0
                            ? t('account.two_factor.trusted_devices', {count: status.trusted_device_count})
                            : t('account.two_factor.no_devices')}
                    </span>
                    {/* Offering "forget all" with nothing to forget is a button that does nothing. */}
                    {status.trusted_device_count > 0 &&
                        <Button label={t('account.two_factor.forget_devices')} icon="pi pi-desktop"
                                className="p-button-outlined p-button-sm" onClick={forgetDevices}
                                disabled={disabled}/>}
                </div>

                <p className="acc-cat">{t('account.two_factor.danger_heading')}</p>
                {/* The password field appears only once the user has asked to turn 2FA off,
                    so a status page does not permanently display an empty credential form. */}
                {!confirmingDisable &&
                <div className="tf-actions">
                    <Button label={t('account.two_factor.turn_off')} icon="pi pi-times"
                            className="p-button-outlined p-button-danger p-button-sm"
                            onClick={() => setConfirmingDisable(true)} disabled={disabled}/>
                </div>}
                {confirmingDisable &&
                <div>
                    <div className="acc-field tf-narrow-field">
                        <label htmlFor="disable_password">{t('account.two_factor.password_to_disable')}</label>
                        <Password id="disable_password" feedback={false} value={password}
                                  onChange={(e) => setPassword(getKeyValue(e).value)}/>
                    </div>
                    <div className="tf-actions">
                        <Button label={t('account.two_factor.turn_off')} icon="pi pi-times"
                                className="p-button-danger p-button-sm" onClick={disable}
                                disabled={disabled || !password}/>
                        <Button label={t('actions.cancel')} className="p-button-text p-button-sm"
                                onClick={() => { setConfirmingDisable(false); setPassword(''); }}
                                disabled={disabled}/>
                    </div>
                </div>}
            </div>}

            {status && !status.is_enabled && !enrollment &&
            <div>
                {/* The explanation sits above the choice, so it is read before deciding. */}
                <p className="acc-section-sub">{t('account.two_factor.method_hint')}</p>
                <button type="button" className="tf-method" disabled={disabled}
                        onClick={() => startSetup('totp')}>
                    <i className="pi pi-mobile"/>
                    <span className="tf-method-text">
                        <span className="tf-method-title">{t('account.two_factor.use_authenticator')}</span>
                        <span className="tf-method-sub">{t('account.two_factor.method_totp_sub')}</span>
                    </span>
                </button>
                <button type="button" className="tf-method" disabled={disabled}
                        onClick={() => startSetup('email')}>
                    <i className="pi pi-envelope"/>
                    <span className="tf-method-text">
                        <span className="tf-method-title">{t('account.two_factor.use_email')}</span>
                        <span className="tf-method-sub">{t('account.two_factor.method_email_sub')}</span>
                    </span>
                </button>
            </div>}

            {enrollment &&
            <div>
                {enrollment.method === 'totp' &&
                <div>
                    <p className="acc-section-sub">{t('account.two_factor.scan_hint')}</p>
                    <QRCodeSVG value={enrollment.provisioning_uri} size={168} includeMargin={true}/>
                    <p className="acc-section-sub tf-manual-hint">{t('account.two_factor.manual_hint')}</p>
                    <p><span className="tf-secret">{enrollment.secret}</span></p>
                </div>}
                {enrollment.method === 'email' &&
                <p className="acc-section-sub">
                    {t('account.two_factor.email_sent', {email: enrollment.masked_email})}
                </p>}

                <div className="acc-field tf-narrow-field">
                    <label htmlFor="activation_code">{t('account.two_factor.code_label')}</label>
                    <InputText id="activation_code" inputMode="numeric" maxLength={6} value={code}
                               onChange={(e) => setCode(getKeyValue(e).value.replace(/\D/g, ''))}/>
                </div>
                <div className="tf-actions">
                    <Button label={t('actions.verify')} icon="pi pi-check" className="p-button-sm"
                            onClick={activate} disabled={disabled || code.length !== 6}/>
                    <Button label={t('actions.cancel')} className="p-button-text p-button-sm"
                            onClick={() => { setEnrollment(null); setCode(''); }} disabled={disabled}/>
                </div>
            </div>}
        </div>
    );
};
export default TwoFactor;
